// Texture preloader - loads textures in parallel and caches them for reuse

import * as THREE from 'three'
import { debug } from '@/utils/debug'

/**
 * TexturePreloader - singleton for parallel texture loading with caching
 * Similar to ModelLoader but for textures (billboard, top-view, ground)
 */
class TexturePreloader {
  private cache: Map<string, THREE.Texture> = new Map()
  private loadingPromises: Map<string, Promise<THREE.Texture>> = new Map()
  private loader: THREE.TextureLoader = new THREE.TextureLoader()

  /**
   * Preload multiple textures in parallel
   * Uses Promise.allSettled so failures don't block other textures
   */
  async preloadMany(urls: string[]): Promise<void> {
    // Deduplicate URLs
    const uniqueUrls = [...new Set(urls.filter(url => url && url.trim() !== ''))]

    if (uniqueUrls.length === 0) {
      debug.log('Texture', 'No textures to preload')
      return
    }

    debug.log('Texture', `Preloading ${uniqueUrls.length} textures in parallel...`)

    const results = await Promise.allSettled(uniqueUrls.map(url => this.load(url)))

    // Count successes and failures
    const succeeded = results.filter(r => r.status === 'fulfilled').length
    const failed = results.filter(r => r.status === 'rejected').length

    if (failed > 0) {
      debug.warn('Texture', `Preloaded ${succeeded}/${uniqueUrls.length} textures (${failed} failed)`)
    } else {
      debug.success('Texture', `Preloaded all ${succeeded} textures`)
    }
  }

  /**
   * Load a single texture with caching
   * Returns cached texture if available, or loads and caches it
   */
  async load(url: string): Promise<THREE.Texture> {
    // Return cached texture
    if (this.cache.has(url)) {
      return this.cache.get(url)!
    }

    // Return in-progress load
    if (this.loadingPromises.has(url)) {
      return this.loadingPromises.get(url)!
    }

    // Start new load
    const promise = new Promise<THREE.Texture>((resolve, reject) => {
      this.loader.load(
        url,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace
          this.cache.set(url, texture)
          this.loadingPromises.delete(url)
          resolve(texture)
        },
        undefined,
        (error) => {
          this.loadingPromises.delete(url)
          debug.warn('Texture', `Failed to load texture: ${url}`, error)
          reject(error)
        }
      )
    })

    this.loadingPromises.set(url, promise)
    return promise
  }

  /**
   * Get a preloaded texture from cache (synchronous)
   * Returns undefined if texture is not in cache
   */
  get(url: string): THREE.Texture | undefined {
    return this.cache.get(url)
  }

  /**
   * Check if a texture is cached
   */
  has(url: string): boolean {
    return this.cache.has(url)
  }

  /**
   * Get cache statistics
   */
  get stats(): { cached: number; loading: number } {
    return {
      cached: this.cache.size,
      loading: this.loadingPromises.size
    }
  }

  /**
   * Clear all cached textures
   */
  clear(): void {
    for (const texture of this.cache.values()) {
      texture.dispose()
    }
    this.cache.clear()
    this.loadingPromises.clear()
    debug.log('Texture', 'Texture cache cleared')
  }
}

// Singleton instance
export const texturePreloader = new TexturePreloader()
